import React, {Component, ReactNode} from 'react'
import {createResidentPage, ResidentPagePropsSet} from '../shared/hoc/createResidentPage'
import {StyledPageTitle} from '../reservations/components/CommonStyle'
import {currentMedia, HideInPC, HideInSP} from '../../styledComponents/ResponsiveConvenience'
import PPStepper from '../shared/PPStepper'
import styled from 'styled-components'
import {Colors, FontSize, FontWeights} from '../../styledComponents/Constants'
import {PlainStyledButton, StyledSecondaryButton} from '../../styledComponents/Button'
import {Box} from '@mui/material'
import {FlexContainer} from '../../styledComponents/FlexComponents'
import {Nullable} from '../../app/TypeHelpers'
import {VOTING_PREFERENCE_RESPONSE, VOTING_PREFERENCE_STATUS} from '../../CodeMaster'
import {EventAvailable, HowToVote} from '@mui/icons-material'
import ResidentLabeledContentComponent from '../shared/ResidentLabeledContentComponent'
import ResidentSectionComponent from '../shared/ResidentSectionComponent'
import {PlainBackLink} from '../../styledComponents/Links'
import {DisplayItemDividerForPageTop} from '../../styledComponents/DisplayItem'
import {NatAsyncCallFunction} from '../../__NoAutomatedTests/common/util/Natutil'
import ResidentGeneralMeetingRepo, {ResidentVotingPreference} from './ResidentGeneralMeetingRepo'
import {ResidentVotingPreferenceProxyInputLocationState} from './ResidentVotingPreferenceProxyInputPage'
import {
    ResidentVotingPreferenceProposalResponseInputLocationState
} from './ResidentVotingPreferenceProposalResponseInputPage'
import {GeneralMeetingProposal} from './model/GeneralMeetingProposal'

export const votingPreferenceStepsForPc: string[] = [
    '総会参加方法の選択',
    '意思表明（出席、委任、議決権行使）',
    '意思表明の確認',
    '完了',
]

export const votingPreferenceStepsForSp: string[] = [
    '選択',
    '意思表明',
    '確認',
    '完了',
]

interface Props {
    condominiumCd: string
    generalMeetingId: number
    assocId: number
    residentGeneralMeetingRepo: ResidentGeneralMeetingRepo
}

interface State {
    generalMeetingDate: Nullable<Date>
    residentVotingPreference: Nullable<ResidentVotingPreference>
    generalMeetingProposalList: GeneralMeetingProposal[]
    beforeNetRespondedAssocIdList: number[]
    isLoadCompleted: boolean
}

class ResidentVotingPreferenceMethodSelectionPage extends Component<Props & ResidentPagePropsSet, State> {
    constructor(props: Props & ResidentPagePropsSet) {
        super(props)

        this.state = {
            generalMeetingDate: null,
            residentVotingPreference: null,
            generalMeetingProposalList: [],
            beforeNetRespondedAssocIdList: [],
            isLoadCompleted: false,
        }

        this.handleAttendanceButtonClick = this.handleAttendanceButtonClick.bind(this)
        this.handleProxyButtonClick = this.handleProxyButtonClick.bind(this)
        this.handleVoteButtonClick = this.handleVoteButtonClick.bind(this)
        this.handleBackButtonClick = this.handleBackButtonClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        const {
            navigate,
            condominiumCd,
            generalMeetingId,
            assocId,
            residentGeneralMeetingRepo,
            eventEmitter,
        } = this.props

        await eventEmitter.showLoading()
        try {
            const response = await NatAsyncCallFunction(
                residentGeneralMeetingRepo.getResidentGeneralMeeting.bind(
                    residentGeneralMeetingRepo,
                    condominiumCd,
                    generalMeetingId,
                ),
                navigate,
            )

            if (response === null) {
                return
            }

            const residentGeneralMeeting = response.details

            if (!residentGeneralMeeting) {
                throw Error('総会情報が無い')
            }

            const beforeNetRespondedAssocIdList = residentGeneralMeeting.residentVotingPreferenceList
                .filter(
                    residentVotingPreference =>
                        residentVotingPreference.votingPreferenceStatus.value ===
                        VOTING_PREFERENCE_STATUS.BEFORE_NET_RESPONDED.value &&
                        residentVotingPreference.assocId !== assocId,
                )
                .map(residentVotingPreference => residentVotingPreference.assocId)

            const selectedResidentVotingPreference = residentGeneralMeeting.residentVotingPreferenceList
                .find(residentVotingPreference => residentVotingPreference.assocId === assocId)

            if (!selectedResidentVotingPreference) {
                throw Error('対象の意思表明が無い')
            }

            const selectedGeneralMeetingProposalsOfAssoc = residentGeneralMeeting.generalMeetingProposalsOfAssocList
                .find(generalMeetingProposalsOfAssoc => generalMeetingProposalsOfAssoc.assocId === assocId)

            if (!selectedGeneralMeetingProposalsOfAssoc) {
                throw Error('対象管理組合の議案情報が無い')
            }

            this.setState({
                generalMeetingDate: residentGeneralMeeting.generalMeetingDate,
                residentVotingPreference: selectedResidentVotingPreference,
                generalMeetingProposalList: selectedGeneralMeetingProposalsOfAssoc.generalMeetingProposalList,
                beforeNetRespondedAssocIdList: beforeNetRespondedAssocIdList,
                isLoadCompleted: true,
            })
        } finally {
            eventEmitter.closeLoading()
        }
    }

    handleAttendanceButtonClick(): void {
        const {
            condominiumCd,
            generalMeetingId,
            assocId,
            navigate
        } = this.props

        const {
            generalMeetingDate,
            residentVotingPreference,
            beforeNetRespondedAssocIdList,
        } = this.state

        if (!generalMeetingDate) {
            throw Error('総会開催日が無い')
        }

        if (!residentVotingPreference) {
            throw Error('対象の意思表明が無い')
        }

        const locationState = {
            generalMeetingDate: generalMeetingDate,
            votingPreferenceId: residentVotingPreference.votingPreferenceId,
            assocId: residentVotingPreference.assocId,
            assocName: residentVotingPreference.assocName,
            votingPreferenceResponse: VOTING_PREFERENCE_RESPONSE.ATTENDANCE,
            votingPreferenceVersion: residentVotingPreference.version,
            beforeNetRespondedAssocIdList: beforeNetRespondedAssocIdList,
        }

        navigate(
            `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/confirmation/attendance`,
            {
                state: locationState,
            },
        )
    }

    handleProxyButtonClick(): void {
        const {
            condominiumCd,
            generalMeetingId,
            assocId,
            navigate
        } = this.props

        const {
            residentVotingPreference,
            beforeNetRespondedAssocIdList,
        } = this.state

        if (!residentVotingPreference) {
            throw Error('対象の意思表明が無い')
        }

        const locationState: ResidentVotingPreferenceProxyInputLocationState = {
            votingPreferenceId: residentVotingPreference.votingPreferenceId,
            assocId: residentVotingPreference.assocId,
            assocName: residentVotingPreference.assocName,
            votingPreferenceResponse: VOTING_PREFERENCE_RESPONSE.PROXY,
            residentProxyVoter: residentVotingPreference.residentProxyVoter,
            votingPreferenceVersion: residentVotingPreference.version,
            beforeNetRespondedAssocIdList: beforeNetRespondedAssocIdList,
        }

        navigate(
            `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/input/proxy`,
            {
                state: locationState,
            },
        )
    }

    handleVoteButtonClick(): void {
        const {
            condominiumCd,
            generalMeetingId,
            assocId,
            navigate
        } = this.props

        const {
            residentVotingPreference,
            generalMeetingProposalList,
            beforeNetRespondedAssocIdList,
        } = this.state

        if (!residentVotingPreference) {
            throw Error('対象の意思表明が無い')
        }

        if (!generalMeetingProposalList) {
            throw Error('対象管理組合の議案リストが無い')
        }

        const locationState: ResidentVotingPreferenceProposalResponseInputLocationState = {
            votingPreferenceId: residentVotingPreference.votingPreferenceId,
            assocId: residentVotingPreference.assocId,
            assocName: residentVotingPreference.assocName,
            votingPreferenceResponse: VOTING_PREFERENCE_RESPONSE.VOTING,
            residentProposalResponseList: residentVotingPreference.residentProposalResponseList,
            votingPreferenceVersion: residentVotingPreference.version,
            generalMeetingProposalList: generalMeetingProposalList,
            beforeNetRespondedAssocIdList: beforeNetRespondedAssocIdList,
        }

        navigate(
            `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}/assocs/${assocId}/voting-preference/input/proposal-response`,
            {
                state: locationState,
            },
        )
    }

    handleBackButtonClick(): void {
        const {
            condominiumCd,
            generalMeetingId,
            navigate
        } = this.props

        navigate(
            `/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`,
        )
    }

    render(): ReactNode {
        const {isLoadCompleted} = this.state

        if (!isLoadCompleted) {
            return null
        }

        const {
            condominiumCd,
            generalMeetingId,
        } = this.props

        const {
            residentVotingPreference,
        } = this.state

        if (!residentVotingPreference) {
            throw Error('対象の意思表明が無い')
        }

        return <>
            <PlainBackLink to={`/resident/condominiums/${condominiumCd}/general-meetings/${generalMeetingId}`}>
                戻る
            </PlainBackLink>
            <StyledDisplayItemDividerForPageTop/>
            <HideInSP>
                <PPStepper steps={votingPreferenceStepsForPc} activeStep={0}/>
            </HideInSP>
            <HideInPC>
                <PPStepper steps={votingPreferenceStepsForSp} activeStep={0} alternativeLabel/>
            </HideInPC>
            <Box height={'30px'}/>
            <StyledPageTitle>総会参加方法の選択</StyledPageTitle>
            <Box height={'20px'}/>
            <div>
                総会への参加方法について、いずれかをご選択ください。ご欠席の場合は、素敵ネットから委任のお手続きと、議決権行使を行えます。
            </div>
            <Box height={'20px'}/>
            <ResidentSectionComponent
                title={'対象管理組合'}
                content={
                    <AssocName>{residentVotingPreference.assocName}</AssocName>
                }
            />
            <VotingPreferenceButtonContainer>
                <StyledButton onClick={this.handleAttendanceButtonClick}>
                    <EventAvailable style={{fontSize: '38px', color: Colors.Primary}}/>
                    <ButtonText>
                        出席する
                    </ButtonText>
                </StyledButton>
                <StyledButton onClick={this.handleProxyButtonClick}>
                    <img
                        src={'/resources/images/icons/icon_approval_delegation.svg'}
                        alt={'委任ロゴ'}
                        style={{width: '38px'}}
                    />
                    <ButtonText>
                        委任する
                    </ButtonText>
                </StyledButton>
                <StyledButton onClick={this.handleVoteButtonClick}>
                    <HowToVote style={{fontSize: '38px', color: Colors.Primary}}/>
                    <ButtonText>
                        議決権を行使する
                    </ButtonText>
                </StyledButton>
            </VotingPreferenceButtonContainer>
            <Box height={'20px'}/>
            <StyledSecondaryButton onClick={this.handleBackButtonClick}>
                戻る
            </StyledSecondaryButton>
            <Box height={'15px'}/>
        </>
    }
}

export default createResidentPage(ResidentVotingPreferenceMethodSelectionPage)

const StyledDisplayItemDividerForPageTop = styled(DisplayItemDividerForPageTop)`
  margin-top: 14px;
  margin-bottom: 15px;
`

const AssocName = styled.div`
  font-weight: ${FontWeights.Normal};
  overflow-wrap: anywhere;
  line-break: anywhere;
`

const VotingPreferenceButtonContainer = styled(FlexContainer)`
  flex-direction: row;
  gap: 20px;
  align-items: center;
  width: 100%;
  padding: 20px 15px;

  ${currentMedia.lessThan('sp')`
    flex-direction: column;
    padding: 20px 0
  `}
`

const StyledButton = styled(PlainStyledButton)`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  height: 100px;
  max-width: 354px;
  width: 100%;
  padding-left: 24px;

  font-size: ${FontSize.Body};
  font-weight: ${FontWeights.Bold};
  border: none;
  background: ${Colors.Surface};
  box-shadow: 0 2px 2px 0 rgba(112, 112, 112, 0.4);
  border-radius: 8px;
  color: ${Colors.Body};

  :active {
    background: linear-gradient(0deg, rgba(112, 112, 112, 0.75), rgba(112, 112, 112, 0.75)), #D9D9D9;
    box-shadow: none;
  }

  :disabled {
    pointer-events: none;
    background-color: ${Colors.AccentLight};
  }
`

const ButtonText = styled.div`
  margin-left: 20px;
`
